import { createElement } from 'react'
import ReactDOM from 'react-dom'

import PageGlobals from '@aytm/app/stats/lib/page/page-globals'

import { CAPABILITY, can, tierFromFeatures } from '@aytm/app/stats/page/data-quality-report/lib/tiers'
import { getFeatures } from '@aytm/app/stats/lib/stat-page'
import { isFilterApplied } from '@aytm/app/stats/page/components/lib/utils'
import Controller from '@aytm/app/stats/page/data-quality-report/controller/controller'

import DataQualityReport from '@aytm/app/stats/page/data-quality-report'

// Everything the embedded data-centrifuge Controller needs from the Backbone page,
// injected as deps so the embedded DC app behaves identically inside the DQR. This shim
// absorbed the retired data-centrifuge view shim. The Controller never touches page.* directly.
const calcDcDeps = page => ({
  // Derived from the boot value, so the legacy page no longer has to hand the endpoint over.
  get dqrUrl() {
    return `${PageGlobals.STAT_SERVER_URL}/dqr`
  },
  getMode: () => page.model.get('mode'),
  getSurveyId: () => PageGlobals.SURVEY_ID,
  getSurveyConfig: () => window.survey?.config,
  getApiKey: () => page.model.get('apiKey'),
  isApiKeyInvalid: () => page.model.isApiKeyInvalid(),
  getFeatures: () => page.model.features,
  getWidth: () => page.model.width(),
  fetchApiKey: opts => page.bridge.fetchApiKey(opts),
  delayFetchApiKey: opts => page.bridge.delayFetchApiKey(opts),
  onFetch: (event, fetchDataCentrifuge) => page.view.onFetch(event, fetchDataCentrifuge),
  renderSidebar: () => page.sidebar.renderDataCentrifugeProps(true),
  applyUserFilter: (pattern, filter) => page.sidebar.applyUserFilter(pattern, filter),
  // The term report lives in the charts state, while the DQR runs in the 'data-quality' one. The mode
  // flips to charts here rather than through the panel switch, whose leave would redraw the report
  // being left; the term report's own route draws the charts. When the term report is the one open
  // there is no route to run, so the panel switch's leave is what draws it.
  openTermReport: () => {
    if ('term-report' === page.model.get('reportId')) {
      page.sidebar.onPanelChange(page.sidebar.defaultSwitchName)
      return
    }

    page.view.switchToCharts()
    page.sidebar.onPanelChange(page.sidebar.defaultSwitchName)
    page.sidebar.onReportIdChange('term-report')
  },
  // A vector explanation can cite the question it fired on. Following that citation leaves the
  // quality tab: return the sidebar to its default panel, then scroll the charts view to the
  // question. Data Quality takes the questions down, so leaving it the jump waits for the redraw.
  scrollToQuestion: (id, batch) => {
    if ('data-quality' === page.model.get('mode')) {
      page.view.queueScrollToQuestion(id, batch)
      page.sidebar.onPanelChange(page.sidebar.defaultSwitchName)
      return
    }
    page.sidebar.onPanelChange(page.sidebar.defaultSwitchName)
    page.view.scrollToQuestion(id, batch)
  }
})

// Pre-fetch seed only. Once the embedded Controller's /dqr fetch returns, the container reads the
// live report from dcController.getReport(); these props are the first-paint fallback.
const readReportProps = () => ({
  tier: tierFromFeatures(getFeatures()),
  fielding: { dataCentrifuge: false, humanReview: false, surveyPerformance: false },
  data: {}
})

// Registered as page.classes.DataCentrifugeView (the stats tab mount). A superset of the
// retired data-centrifuge view shim: it owns the same embedded DC Controller + page.model
// subscriptions and forwards the same lifecycle the page_router / sidebar_view call, while
// also rendering the surrounding five DQR sections.
class DataQualityReportShim {
  constructor(options = {}) {
    const page = window.StatPage
    this.page = page
    // page_router passes a jQuery object as el — unwrap to the DOM node.
    this.el = options.el?.[0] || options.el

    this.controller = new Controller(this, calcDcDeps(page))
    this.controller.init()

    // Fetch only when the Quality tab is the view we opened on. checkData() is the same mode-guarded
    // entry the change:state listener uses, so opening the tab later kicks off the fetch through the
    // one path — and a page that never opens it (charts, a share link) issues no /dqr at all. It runs
    // before the mount for the reason initListeners gives.
    this.controller.checkData()

    if (this.el) {
      this.renderShown()
      // The report tells a reader below T0 that the stats-page filter does not reach it, so its note
      // follows the filter while the tab shows it; opening the tab draws it again (initListeners).
      page.model.on('change:filtersPattern', () => this.renderShown(), this)
    }
  }

  render() {
    const props = {
      ...readReportProps(),
      filterActive: isFilterApplied(this.page.model.get('filtersPattern')),
      dcController: this.controller
    }
    ReactDOM.render(createElement(DataQualityReport, props), this.el)
  }

  // The view lives as long as the page, but its markup exists only while the Quality tab shows it, the
  // way comments and charts drop theirs on leaving. A Data Centrifuge run outlives the tab in the
  // controller, not here.
  renderShown() {
    if (!this.el) return
    if ('data-quality' === this.page.model.get('mode')) {
      this.render()
    } else if (ReactDOM.unmountComponentAtNode(this.el)) {
      // the unmounted section can no longer publish its open state, so the sidebar must not keep the last one
      this.controller.setSectionOpen(false)
    }
  }

  // sidebar_view reads `page.dataCentrifugeView.listProps`; the Controller owns the object.
  get listProps() {
    return this.controller.listProps
  }

  // sidebar_view keeps Data Quality open beside Filters on it; the tier is the one every surface shares.
  get followsStatsFilter() {
    return can(this.controller.resolveTier(), CAPABILITY.FILTER_RESPONSES)
  }

  // page_router owns the change:mode subscription via the bridge, forwarding to the Controller.
  initListeners() {
    // The router and the view's mode switches set the mode before the state, so entering the tab finds
    // it already 'data-quality' and leaving it finds the new mode: the report mounts or unmounts here.
    // The controller goes first: it restores a held payload synchronously, and a tree mounted before
    // that would miss the notify, since it subscribes only once its effects run.
    this.page.model.on(
      'change:state',
      () => {
        this.controller.checkData()
        this.renderShown()
      },
      this
    )
  }

  checkData() {
    this.controller.checkData()
  }

  delayFetchJob(includeVectors = null) {
    this.controller.delayFetchJob(includeVectors)
  }

  destroyReactComponents() {
    this.controller.destroyViews()
  }

  onDestroy() {
    this.page.model.off(null, null, this)
    this.controller.destroy()
    if (this.el) {
      ReactDOM.unmountComponentAtNode(this.el)
    }
    return true
  }
}

// Register on page.classes so page_router's `new page.classes.DataQualityView({el})`
// resolves to this bridge. Idempotent; safe whichever bundle evaluates first.
export const registerDataQualityReportView = () => {
  const page = (window.StatPage = window.StatPage || {})
  page.classes = page.classes || {}
  page.classes.DataQualityView = DataQualityReportShim
}

export default DataQualityReportShim
